import {
  Music,
  Shield,
  Hash,
  Bot,
  Users,
  Activity,
} from "lucide-react";

const features = [
  {
    icon: Music,
    title: "Music Engine",
    desc: "High-quality streaming from YouTube. Queue, skip, shuffle — full control from the dashboard.",
    span: "md:col-span-7",
    accent: true,
  },
  {
    icon: Bot,
    title: "Gemini 3 AI",
    desc: "Multilingual chat. Members talk to the bot in any language they prefer.",
    span: "md:col-span-5",
  },
  {
    icon: Hash,
    title: "Channels",
    desc: "Create, edit, and delete text/voice channels with one click.",
    span: "md:col-span-4",
  },
  {
    icon: Shield,
    title: "Moderation",
    desc: "Kick, ban, mute, and protect your community with automated safeguards.",
    span: "md:col-span-4",
  },
  {
    icon: Users,
    title: "Welcome Auto",
    desc: "Customize welcome messages and auto-roles for new recruits.",
    span: "md:col-span-4",
  },
  {
    icon: Activity,
    title: "Live Stats & Logs",
    desc: "Every command, every action — captured in a tactical activity feed.",
    span: "md:col-span-12",
  },
];

export default function FeaturesGrid() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative max-w-7xl mx-auto px-6 md:px-12 py-28"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div>
          <div className="overline mb-3">// 02 · CAPABILITIES</div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight max-w-2xl">
            Built for squads who actually <span className="text-brand-orange">play to win.</span>
          </h2>
        </div>
        <div className="text-zinc-400 max-w-md text-base">
          Six modules. One bot. Zero compromises. Every feature accessible from
          a unified web cockpit, designed for fast operational decisions during
          live ops.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {features.map((f, i) => {
          const Icon = f.icon;
          return (
            <div
              key={f.title}
              className={`tactical-card p-7 ${f.span} ${f.accent ? "md:row-span-2" : ""}`}
              data-testid={`feature-card-${i}`}
            >
              <div className="flex items-start justify-between mb-5">
                <div className={`p-3 ${f.accent ? "bg-brand-orange text-black" : "border border-brand-orange/40 text-brand-orange"}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-mono-tabular text-xs text-zinc-600">
                  {String(i + 1).padStart(2, "0")} / {features.length.toString().padStart(2, "0")}
                </div>
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2">
                {f.title}
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{f.desc}</p>

              {f.accent && (
                <div className="mt-8 border-t border-brand-line pt-6">
                  <div className="space-y-2 font-mono-tabular text-xs">
                    {[
                      "▸ Lossless audio quality",
                      "▸ Persistent queue across reboots",
                      "▸ Per-server volume profiles",
                      "▸ Auto-DJ playlists",
                    ].map((item) => (
                      <div key={item} className="text-zinc-500">
                        <span className="text-brand-orange">{item.slice(0, 2)}</span>
                        {item.slice(2)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
